import { InvestmentPaymentCardStyles } from './InvestmentPaymentCard.styles.tsx';
import { Button, Flex, Text, useMantineTheme } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { InvestmentDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/InvestmentDetailsCard.tsx';
import {
	formatPaymentAmount,
	getPaymentStatusColor,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/Table.utils.tsx';
import {
	CurrencyType,
	InvestmentDetailsLoan,
	TransactionState,
} from '@dealstack/shared/lib/generated';
import { useNavigate } from 'react-router-dom';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { useState } from 'react';
import { InvestmentPaymentStatusDropdown } from './InvestmentPaymentStatusDropdown.tsx';
import { ManageLoanDrawer } from '../ManageLoanDrawer/ManageLoanDrawer.tsx';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';

interface Props {
	investmentWorkflowId: string;
	cost: number;
	currency: CurrencyType;
	status: TransactionState;
	annotation: string | null;
	loans: InvestmentDetailsLoan[];
	isReleased: boolean;
}

export const InvestmentPaymentCard = ({
	investmentWorkflowId,
	cost,
	currency,
	status,
	annotation,
	loans,
	isReleased,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const navigate = useNavigate();
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const mantineTheme = useMantineTheme();
	const [isLoanDrawerOpen, setIsLoanDrawerOpen] = useState<boolean>(false);
	const [managedLoan, setManagedLoan] = useState<InvestmentDetailsLoan | null>(null);

	const loanAmount = loans.reduce((acc, loan) => acc + loan.amount, 0);
	const cashAmount = cost > 0 ? Math.max(0, cost - loanAmount) : 0;

	return (
		<InvestmentDetailsCard cardLabel={tMipManage('investment_payment_card.payment.card_label')}>
			<InvestmentPaymentCardStyles>
				<Flex direction="column" p="0 0.5rem">
					<Text c="dark.0" mb="0.25rem">
						{tMipManage('investment_payment_card.total_consideration')}
					</Text>

					<Text fz="lg" fw={600} c="dark.8">
						{formatPaymentAmount(cost, currency, 'symbol')}
					</Text>
				</Flex>

				<div className="payment-item">
					<Flex justify="space-between">
						<Text
							fz="sm"
							fw={600}
							c="gray.9"
							className="payment-item-label payment-item-label-cash"
							tt="uppercase"
						>
							<figure
								style={{
									...(cashAmount > 0 && {
										background: getPaymentStatusColor(status),
									}),
								}}
								aria-hidden
							/>

							{tMipManage('investment_payment_card.payment_item_label.cash')}
						</Text>

						{cashAmount > 0 ? (
							<InvestmentPaymentStatusDropdown
								investmentWorkflowId={investmentWorkflowId}
								status={status}
								annotation={annotation}
								isReleased={isReleased}
							/>
						) : (
							<Button
								variant="outline"
								size="xs"
								c="dark.8"
								styles={{
									root: {
										borderColor: mantineTheme.colors.gray[4],
										width: '2rem',
										height: '2rem',
									},
								}}
								onClick={() =>
									navigate({
										pathname: mipManagePath(MIP_MANAGE_ROUTES.PAYMENTS, {
											dealId,
											mipId,
										}),
										search: `?investmentId=${encodeURIComponent(investmentWorkflowId)}`,
									})
								}
							>
								<Icon variant="arrow-up-right-2" size="md" />
							</Button>
						)}
					</Flex>

					{cashAmount > 0 && (
						<Text fw={600} c="dark.8">
							{formatPaymentAmount(cashAmount, currency, 'symbol')}
						</Text>
					)}
				</div>

				{!loans?.length && (
					<div className="payment-item">
						<Flex justify="space-between">
							<Text
								fz="sm"
								fw={600}
								c="gray.9"
								className="payment-item-label payment-item-label-loan"
								tt="uppercase"
							>
								<figure aria-hidden />

								{tMipManage('investment_payment_card.payment_no_loan')}
							</Text>

							<Button
								variant="default"
								size="xs"
								leftSection={<Icon variant="plus" size="lg" />}
								styles={{ root: { padding: '0.375rem 0.5rem' } }}
								onClick={() => setIsLoanDrawerOpen(true)}
								disabled={isReleased}
							>
								<Text fw={400} fz="md">
									{tMipManage('investment_payment_card.payment_loan.add_button')}
								</Text>
							</Button>
						</Flex>
					</div>
				)}

				{loans?.map((loan) => (
					<div key={loan.id} className="payment-item">
						<Flex justify="space-between" gap="0.5rem" align="center">
							<Text
								fz="sm"
								fw={600}
								c="gray.9"
								className="payment-item-label payment-item-label-loan"
								tt="uppercase"
								title={loan.name}
								style={{ minWidth: 0, flex: 1 }}
							>
								<figure
									style={{ background: mantineTheme.colors.violet[8] }}
									aria-hidden
								/>

								<span
									style={{
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										display: 'block',
										minWidth: 0,
										flex: 1,
									}}
								>
									{loan.name}
								</span>
							</Text>

							<Button
								variant="default"
								style={{ flexShrink: 0 }}
								styles={{
									root: {
										width: '2rem',
										height: '2rem',
										padding: 0,
									},
								}}
								onClick={() => {
									setManagedLoan(loan);
									setIsLoanDrawerOpen(true);
								}}
								disabled={isReleased}
							>
								<Icon variant="settings" size="s" />
							</Button>
						</Flex>

						<Flex justify="space-between" mt="0.5rem" gap="0.5rem" align="center">
							<Text fw={600} c="dark.8" style={{ flexShrink: 0 }}>
								{formatPaymentAmount(loan.amount, currency, 'symbol')}
							</Text>

							<Text
								fw={400}
								c="dark.0"
								title={loan.funder}
								style={{
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									display: 'block',
									minWidth: 0,
									flex: 1,
									textAlign: 'right',
								}}
							>
								{tMipManage('investment_payment_card.payment.provider_name', {
									provider: loan.funder,
								})}
							</Text>
						</Flex>
					</div>
				))}
			</InvestmentPaymentCardStyles>
			<ManageLoanDrawer
				opened={isLoanDrawerOpen}
				onClose={() => {
					setIsLoanDrawerOpen(false);
					setManagedLoan(null);
				}}
				loan={managedLoan}
				allLoans={loans}
				currency={currency}
				totalCost={cost}
				loanAmount={loanAmount}
			/>
		</InvestmentDetailsCard>
	);
};
